<template>
  <layout>
    <template v-slot:header>
      <AppHead title="Orders"/>
      <div class="admin-header-container">
        <h2 class="mr-3">Orders</h2>
        <Search :searchText="searchText" @searched="search"/>
      </div>
      <flash-messages/>
    </template>

    <template v-slot:breadcrumbs>
      <BreadCrumbs activeItem="Orders" :crumbs="{ home: 'Dashboard' }"/>
    </template>

    <template v-slot:default>
      <section class="admin-orders">
        <div class="d-flex align-items-center py-1">
          <div class="mr-2">Order Status</div>
          <SelectButton v-model="searchStatus" :options="status" optionLabel="desc" optionValue="id" :multiple="true"
                        @change="(e) => statusFilter(e)"
                        :class="{disabled: onLoading}">
            <template #option="slotProps">
              <div class="status-option">
                <div>{{ slotProps.option.desc }}
                  <span class="status-option-count">{{ slotProps.option.count }}</span>
                </div>
              </div>
            </template>
          </SelectButton>
        </div>
        <div class="d-flex align-items-center py-1">
          <div class="mr-2">Transaction Status</div>
          <SelectButton v-model="searchTransaction" :options="transactionOptions" optionLabel="desc" optionValue="id"
                        :multiple="true" @change="(e) => statusFilter(e)"
                        :class="{disabled: onLoading}">
            <template #option="slotProps">
              <div class="status-option">
                <div>{{ slotProps.option.desc }}
                  <span class="status-option-count">{{ slotProps.option.count }}</span>
                </div>
              </div>
            </template>
          </SelectButton>
        </div>
        <div class="d-flex align-items-center py-1">
          <div class="mr-2">Tags</div>
          <div class="w-100">
            <Multiselect
              placeholder="All Tags"
              v-model="searchTags"
              mode="tags"
              :options="tags"
              searchable
            />
          </div>
        </div>
        <div class="d-flex align-items-center py-1">
          <div class="mr-2">Exclude Tags</div>
          <div class="flex-grow-1">
            <Multiselect
              v-model="excludeTags"
              mode="tags"
              :options="tags"
              searchable
            />
          </div>
        </div>
        <DataTable ref="admin-orders-table" :value="orders" :paginator="true" :rows="20" breakpoint="992px"
                   paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                   currentPageReportTemplate="({first} - {last} of {totalRecords})"
                   paginatorPosition="both" removableSort
                   :rowsPerPageOptions="[10, 20, 50, 100]"
                   :rowClass="() => { return 'table-row' }"
                   sortField="date" :sortOrder="-1"
                   class="p-datatable-sm admin-datatable"
                   tableClass="border-left border-right border-bottom"
                   stateStorage="local" stateKey="admin-orders-table" dataKey="id"
                   @row-click="(e) => this.$inertia.visit(route('admin.order.view', e.data.id))">
          <template #paginatorLeft>
            {{ orders.length }} Orders
          </template>
          <Column field="id" header="Order #">
            <template #body="slotProps">
              {{ slotProps.data.id }}
              <PaymentMethodDisplay :payment_method="slotProps.data.payment.payment_method"/>
              <Tags :tags="slotProps.data.tags"></Tags>
            </template>
          </Column>
          <Column field="status_desc" header="Status" :sortable="true">
          </Column>
          <Column field="mtd_company" header="MTD" :sortable="true"></Column>
          <Column field="customer_name" header="Customer" :sortable="true"></Column>
          <Column field="total" header="Total" :sortable="true">
            <template #body="slotProps">
              {{ formatDollars(slotProps.data.total) }}
            </template>
          </Column>
          <Column field="date" header="Date" :sortable="true">
            <template #body="slotProps">
              {{ moment(slotProps.data.date).format('M/D/YY h:mm A') }}
            </template>
          </Column>
          <Column class="button-container" :exportable="false">
            <template #body="slotProps">
              <jet-button class="btn-short text-nowrap" :goesTo="route('admin.order.view', slotProps.data.id)"
                          icon="fas fa-chevron-right">View
              </jet-button>
            </template>
          </Column>
        </DataTable>
      </section>

      <card headerStyle="primary" title="Orders With Backorder Items" class="my-3">
        <ul class="list-group list-group-flush">
          <li v-for="item in backorder" class="list-group-item px-0 py-2" :key="item.order_id">
            <inertia-link :href="route('admin.order.view', item.order_id)">{{ item.order_id }}</inertia-link>
            <span class="text-secondary ml-2">{{ item.sku }} x   {{ item.qty }} 

                <span v-if="item.inventory_id">
                 ETA: {{ item.next_delivered_at_eta ? moment.utc(item.next_delivered_at_eta).format('M/D/YYYY') : '' }}
                  <Calendar v-model="item.next_delivered_at_eta_update" :minDate="(new Date())" :showIcon="true"
                            :selectOtherMonths="true" @date-select="updateETA(item)"/>
                </span>
            </span>
          </li>
        </ul>
      </card>
    </template>
  </layout>
</template>

<script>
import AppHead from "@/Shared/AppHead";
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Admin/Layout'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import moment from 'moment'
import SelectButton from 'primevue/selectbutton'
import Search from '@/Shared/Admin/Search'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetButton from '@/Jetstream/Button'
import Tags from '@/Components/General/Tags'
import PaymentMethodDisplay from '@/Components/Transaction/PaymentMethodDisplay'
import Calendar from 'primevue/calendar'
import Multiselect from "@vueform/multiselect/src/Multiselect";

export default {
  components: {
    AppHead,
    Icon,
    SearchFilter,
    Layout,
    SelectButton,
    FlashMessages,
    Search,
    BreadCrumbs,
    JetButton,
    Calendar,
    Multiselect,
    Tags,
    PaymentMethodDisplay,
  },
  props: {
    orders: Array,
    filters: Object,
    query: String,
    status: Array,
    selectedStatus: Array,
    tags: Array,
    selectedTags: Array,
    selectedExcludeTags: Array,
    transactionOptions: Array,
    selectedTransactionStatus: Array,
    backorder: Array
  },
  data() {
    return {
      onLoading: false,
      form: {
        search: this.selectedStatus
      },
      updateForm: this.$inertia.form({
        next_delivered_at_eta: null
      }),
      searchTags: this.selectedTags.map(t => t.id),
      excludeTags: this.selectedExcludeTags.map(t => t.id),
      searchText: this.query,
      searchTransaction: this.selectedTransactionStatus,
      searchStatus: this.selectedStatus
    }
  },
  created() {
    this.moment = moment
  },
  watch: {
    excludeTags() {
      this.statusFilter();
    },
    searchTags() {
      this.statusFilter();
    }
  },
  methods: {
    search(e, searchText) {
      this.$refs['admin-orders-table'].d_sortField = null
      this.$refs['admin-orders-table'].resetPage()
      this.onLoading = true
      this.$inertia.get(this.route('admin.orders'), {
        query: searchText,
        tags: this.searchTags,
        exclude_tags: this.excludeTags,
        transaction_status: this.searchTransaction,
        status: this.searchStatus
      })
      this.onLoading = false
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    statusFilter(e) {
      if (!this.form.search.includes(-1) && this.searchStatus.includes(-1)) this.searchStatus = [-1];
      else {
        this.searchStatus = this.searchStatus.filter(status => status != -1);
      }

      this.$refs['admin-orders-table'].resetPage()
      this.onLoading = true
      this.$inertia.get(this.route('admin.orders'), {
        query: this.searchText,
        transaction_status: this.searchTransaction,
        tags: this.searchTags,
        exclude_tags: this.excludeTags,
        status: this.searchStatus.length > 0 ? this.searchStatus : [-1]
      })
      this.onLoading = false
    },
    updateETA(item) {
      this.updateForm.next_delivered_at_eta = item.next_delivered_at_eta_update;
      this.updateForm.post(this.route('admin.inventory.updateitemeta', item.inventory_id), {
        preserveScroll: true
      });
    },
  },
}
</script>

<style>
.status-option-count {
  float: right;
  margin-left: 15px;
  width: 40px;
  background-color: #e8e8e8;
  color: #000;
  border-radius: 5px;
}
</style>

<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.admin-orders .multiselect {
  min-height: unset;
  height: calc(1.5em + 0.5rem + 2px);
  font-size: 0.7875rem;
  padding: 1rem 0;
}
</style>
